import {
  DEFAULT_CHART_START_PRICE,
  MIN_FEED_ANIMATION_MS,
  FEED_ANIMATION_RATIO,
  MAX_FEED_ANIMATION_MS,
  SMOOTH_JUMP_DURATION_RATIO_MULTIPLIER,
  SMOOTH_JUMP_BASE_MIN_MS,
  SMOOTH_JUMP_MAX_MS,
  MIN_DISPLAY_PRICE,
  TICK_INTERPOLATION_VOLATILITY_RATIO,
  TICK_INTERPOLATION_MIN_VOLATILITY,
  TICK_SPIKE_TRIGGER_CHANCE,
  TICK_SPIKE_STRENGTH_MIN,
  TICK_SPIKE_STRENGTH_MAX,
  TICK_SPIKE_COOLDOWN_MIN_FRAMES,
  TICK_SPIKE_COOLDOWN_MAX_FRAMES,
  IDLE_MICRO_MOVE_RATIO,
  IDLE_MICRO_MOVE_MAX_RATIO,
} from "./chartConstants";
import { roundPriceByMagnitude } from "../../lib/generateInitialHistory";

export function resolveChartStartPrice(value) {
  return Number.isFinite(value) ? value : DEFAULT_CHART_START_PRICE;
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

export function resolveFeedAnimationDurationMs(previousTickTime, nextTickTime) {
  const deltaSeconds =
    Number.isFinite(previousTickTime) && Number.isFinite(nextTickTime)
      ? Math.max(0, nextTickTime - previousTickTime)
      : 0;
  if (deltaSeconds <= 0) {
    return MIN_FEED_ANIMATION_MS;
  }
  const scaledDuration = Math.round(deltaSeconds * 1000 * FEED_ANIMATION_RATIO);
  return clamp(scaledDuration, MIN_FEED_ANIMATION_MS, MAX_FEED_ANIMATION_MS);
}

export function resolveSmoothJumpDurationMs(fromPrice, toPrice, baseDurationMs) {
  const safeFrom = Math.max(Math.abs(fromPrice), 1);
  const jumpRatio = Math.abs(toPrice - fromPrice) / safeFrom;
  const extraDuration = Math.round(
    jumpRatio * SMOOTH_JUMP_DURATION_RATIO_MULTIPLIER,
  );
  const nextDuration = baseDurationMs + extraDuration;
  return clamp(nextDuration, SMOOTH_JUMP_BASE_MIN_MS, SMOOTH_JUMP_MAX_MS);
}

export function easeInOutCubic(progress) {
  if (progress < 0.5) {
    return 4 * progress * progress * progress;
  }
  const t = -2 * progress + 2;
  return 1 - (t * t * t) / 2;
}

export function interpolateTickDisplayPrice(basePrice, targetPrice, motionState) {
  const safeBase = Math.max(MIN_DISPLAY_PRICE, basePrice);
  const referencePrice = Math.max(
    MIN_DISPLAY_PRICE,
    Number.isFinite(targetPrice) ? targetPrice : safeBase,
  );
  const volatility = Math.max(
    referencePrice * TICK_INTERPOLATION_VOLATILITY_RATIO,
    TICK_INTERPOLATION_MIN_VOLATILITY,
  );
  const acceleration = (Math.random() - 0.5) * volatility * 0.04;
  motionState.velocity = (motionState.velocity + acceleration) * 0.945;
  motionState.phase += 0.075 + Math.random() * 0.025;
  const oscillation =
    Math.sin(motionState.phase) * volatility * 0.48 +
    Math.sin(motionState.phase * 0.36 + 1.2) * volatility * 0.26;
  motionState.noise =
    (motionState.noise + motionState.velocity) * 0.992 + oscillation * 0.11;
  motionState.noise = clamp(
    motionState.noise,
    -volatility * 1.9,
    volatility * 1.9,
  );
  if (motionState.spikeCooldownFrames > 0) {
    motionState.spikeCooldownFrames -= 1;
  } else if (Math.random() < TICK_SPIKE_TRIGGER_CHANCE) {
    const spikeStrength =
      volatility *
      (TICK_SPIKE_STRENGTH_MIN +
        Math.random() * (TICK_SPIKE_STRENGTH_MAX - TICK_SPIKE_STRENGTH_MIN));
    motionState.spike = (Math.random() > 0.5 ? 1 : -1) * spikeStrength;
    motionState.spikeCooldownFrames = Math.floor(
      TICK_SPIKE_COOLDOWN_MIN_FRAMES +
      Math.random() *
      (TICK_SPIKE_COOLDOWN_MAX_FRAMES - TICK_SPIKE_COOLDOWN_MIN_FRAMES + 1),
    );
  }
  motionState.spike *= 0.82;
  const offset = clamp(
    motionState.noise + motionState.spike,
    -volatility * 2.2,
    volatility * 2.2,
  );
  const composedPrice = Math.max(MIN_DISPLAY_PRICE, safeBase + offset);
  const lastDisplay = Number.isFinite(motionState.displayPrice)
    ? motionState.displayPrice
    : safeBase;
  const easedDisplay = lastDisplay + (composedPrice - lastDisplay) * 0.22;
  const maxStep = Math.max(volatility * 0.95, referencePrice * 0.0000016);
  const limitedDisplay =
    lastDisplay + clamp(easedDisplay - lastDisplay, -maxStep, maxStep);
  motionState.displayPrice = Math.max(MIN_DISPLAY_PRICE, limitedDisplay);
  return motionState.displayPrice;
}

export function resolveIdleMicroPrice(previousPrice, motionState, tickSize) {
  const safePrevious = Math.max(MIN_DISPLAY_PRICE, previousPrice);
  const safeTickSize =
    Number.isFinite(tickSize) && tickSize > 0 ? tickSize : 0.01;
  const reference = Math.max(Math.abs(safePrevious), 1);
  const volatility = Math.max(
    reference * IDLE_MICRO_MOVE_RATIO,
    safeTickSize * 1.8,
  );
  const maxMove = Math.max(
    reference * IDLE_MICRO_MOVE_MAX_RATIO,
    safeTickSize * 2.8,
  );
  motionState.phase += 0.55 + Math.random() * 0.18;
  const wave = Math.sin(motionState.phase) * volatility * 0.62;
  const counterWave =
    Math.sin(motionState.phase * 0.43 + 1.7) * volatility * 0.28;
  const randomPulse = (Math.random() - 0.5) * volatility * 0.42;
  motionState.velocity =
    (motionState.velocity ?? 0) * 0.68 +
    (wave + counterWave + randomPulse) * 0.24;
  const delta = clamp(motionState.velocity, -maxMove, maxMove);
  const nextPrice = Math.max(MIN_DISPLAY_PRICE, safePrevious + delta);
  return Math.abs(nextPrice - safePrevious) < safeTickSize * 0.15
    ? safePrevious + (Math.random() > 0.5 ? 1 : -1) * safeTickSize * 0.35
    : nextPrice;
}

export function isMeaningfulPriceChange(previousPrice, nextPrice, tickSize) {
  if (!Number.isFinite(previousPrice) || !Number.isFinite(nextPrice)) {
    return false;
  }
  const safeTickSize =
    Number.isFinite(tickSize) && tickSize > 0 ? tickSize : 0.01;
  const reference = Math.max(Math.abs(previousPrice), Math.abs(nextPrice), 1);
  const threshold = Math.max(safeTickSize * 0.2, reference * 0.0000004);
  return Math.abs(nextPrice - previousPrice) >= threshold;
}

export function applyTickCandleData(targetPoint, sourceTick, fallbackPrice) {
  const open = Number.isFinite(sourceTick?.open)
    ? sourceTick.open
    : fallbackPrice;
  const close = Number.isFinite(sourceTick?.close)
    ? sourceTick.close
    : fallbackPrice;
  const high = Number.isFinite(sourceTick?.high)
    ? sourceTick.high
    : Math.max(open, close);
  const low = Number.isFinite(sourceTick?.low)
    ? sourceTick.low
    : Math.min(open, close);
  if (
    !Number.isFinite(open) ||
    !Number.isFinite(close) ||
    !Number.isFinite(high) ||
    !Number.isFinite(low)
  ) {
    return;
  }
  targetPoint.open = open;
  targetPoint.close = close;
  targetPoint.high = Math.max(high, open, close);
  targetPoint.low = Math.min(low, open, close);
}

export function resolveTimeMarkerCoordinate(time, timeframe, getTimeCoordinate) {
  if (!Number.isFinite(time) || typeof getTimeCoordinate !== "function") {
    return null;
  }
  return getTimeCoordinate(time);
}

export function expand1sToTickHistory(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const expanded = [];

  const refPrice = Math.max(Math.abs(points[0]?.value ?? 0), 1);
  const microAmp = refPrice * 0.000048;

  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[i + 1];
    const t = Math.floor(current.time);

    const cOpen = Number.isFinite(current.open) ? current.open : current.value;
    const cHigh = Number.isFinite(current.high) ? current.high : current.value;
    const cLow = Number.isFinite(current.low) ? current.low : current.value;
    const cClose = Number.isFinite(current.close) ? current.close : current.value;
    const pNext = next
      ? (Number.isFinite(next.close) ? next.close : next.value)
      : cClose;

    const phase = i * 0.73;
    const microAt = (offset) => {
      const p = phase + offset * 0.37;
      return (Math.sin(p) * 0.42
        + Math.sin(p * 2.17 + 1.3) * 0.28
        + Math.sin(p * 3.71 + 2.7) * 0.18
        + Math.sin(p * 0.43 + 4.1) * 0.12) * microAmp;
    };

    const v0 = roundPriceByMagnitude(cOpen + microAt(0));
    expanded.push({
      time: Math.round(t * 10) / 10,
      value: v0,
      open: cOpen,
      high: Math.max(cHigh, v0),
      low: Math.min(cLow, v0),
      close: v0,
    });

    const isBullish = cClose >= cOpen;
    const midBase = isBullish
      ? cOpen + (cHigh - cOpen) * 0.55
      : cOpen - (cOpen - cLow) * 0.55;
    const v1 = roundPriceByMagnitude(midBase + microAt(1));
    expanded.push({
      time: Math.round((t + 0.3) * 10) / 10,
      value: v1,
      open: v0,
      high: Math.max(v0, v1),
      low: Math.min(v0, v1),
      close: v1,
    });

    const closeBase = cClose + (pNext - cClose) * 0.12;
    const v2 = roundPriceByMagnitude(closeBase + microAt(2));
    expanded.push({
      time: Math.round((t + 0.6) * 10) / 10,
      value: v2,
      open: v1,
      high: Math.max(v1, v2),
      low: Math.min(v1, v2),
      close: v2,
    });
  }
  return expanded;
}

export function resolveHistoricalFetchConfig(timeframe) {
  switch (timeframe) {
    case "Tick":
      return { interval: "1s", points: 1000 };
    case "5s":
    case "10s":
      return { interval: "1s", points: 7200 };
    case "1m":
      return { interval: "1m", points: 1440 };
    case "5m":
    case "10m":
    case "15m":
    case "30m":
    case "1h":
    case "1hr":
      return { interval: timeframe === "10m" ? "5m" : (timeframe === "1hr" ? "1h" : timeframe), points: 1200 };
    default:
      return { interval: "1m", points: 1440 };
  }
}

export function buildTradeIdentityKey(order) {
  const id = String(order?.id || order?._id || order?.orderId || "").trim();
  const side = String(order?.side || "").toLowerCase();
  const entryTime = Number(order?.entryTime || order?.time || 0);
  const expiryTime = Number(order?.expiryTime || order?.expiresAt || 0);
  const entryPrice = Number(order?.entryPrice || order?.price || 0);
  const normalizedPrice = Number.isFinite(entryPrice)
    ? entryPrice.toFixed(7)
    : "0.0000000";

  return [
    id,
    side,
    entryTime > 0 ? entryTime.toFixed(1) : "0",
    expiryTime > 0 ? expiryTime.toFixed(1) : "0",
    normalizedPrice,
  ].join(":");
}

export function areActiveOrdersEqual(previousOrders, nextOrders) {
  if (previousOrders === nextOrders) {
    return true;
  }
  if (!Array.isArray(previousOrders) || !Array.isArray(nextOrders)) {
    return !previousOrders && !nextOrders;
  }
  if (previousOrders.length !== nextOrders.length) {
    return false;
  }

  for (let index = 0; index < previousOrders.length; index += 1) {
    const previousOrder = previousOrders[index];
    const nextOrder = nextOrders[index];
    if (buildTradeIdentityKey(previousOrder) !== buildTradeIdentityKey(nextOrder)) {
      return false;
    }
    if (String(previousOrder?.id || "") !== String(nextOrder?.id || "")) {
      return false;
    }
    if (String(previousOrder?.status || "") !== String(nextOrder?.status || "")) {
      return false;
    }
  }

  return true;
}

export function areReferencePriceLinesEqual(previousLines = [], nextLines = []) {
  if (previousLines === nextLines) {
    return true;
  }
  if (previousLines.length !== nextLines.length) {
    return false;
  }
  for (let index = 0; index < previousLines.length; index += 1) {
    const previousLine = previousLines[index];
    const nextLine = nextLines[index];
    if (
      previousLine?.price !== nextLine?.price ||
      previousLine?.color !== nextLine?.color ||
      previousLine?.lineWidth !== nextLine?.lineWidth ||
      previousLine?.lineStyle !== nextLine?.lineStyle ||
      previousLine?.axisLabelVisible !== nextLine?.axisLabelVisible ||
      previousLine?.title !== nextLine?.title
    ) {
      return false;
    }
  }
  return true;
}

export function areTradingChartPropsEqual(previousProps, nextProps) {
  return (
    previousProps.symbol === nextProps.symbol &&
    previousProps.marketSymbol === nextProps.marketSymbol &&
    previousProps.timeframe === nextProps.timeframe &&
    previousProps.chartType === nextProps.chartType &&
    previousProps.drawingEnabled === nextProps.drawingEnabled &&
    previousProps.drawingTool === nextProps.drawingTool &&
    previousProps.drawingsResetKey === nextProps.drawingsResetKey &&
    previousProps.disableInteractions === nextProps.disableInteractions &&
    previousProps.hideTimeScale === nextProps.hideTimeScale &&
    previousProps.disableZoomToLatest === nextProps.disableZoomToLatest &&
    previousProps.feedMode === nextProps.feedMode &&
    previousProps.randomWalkStep === nextProps.randomWalkStep &&
    previousProps.randomWalkIntervalMs === nextProps.randomWalkIntervalMs &&
    previousProps.fixedZoomPercent === nextProps.fixedZoomPercent &&
    previousProps.keepLatestCentered === nextProps.keepLatestCentered &&
    previousProps.centeredVisibleBars === nextProps.centeredVisibleBars &&
    previousProps.tickVisibleHistoryPoints ===
    nextProps.tickVisibleHistoryPoints &&
    previousProps.accurateLivePrice === nextProps.accurateLivePrice &&
    previousProps.forwardSpaceBars === nextProps.forwardSpaceBars &&
    previousProps.forceTickEveryMs === nextProps.forceTickEveryMs &&
    previousProps.smoothContinuousMotion === nextProps.smoothContinuousMotion &&
    previousProps.smoothShiftDurationMs === nextProps.smoothShiftDurationMs &&
    previousProps.directionalOverlay === nextProps.directionalOverlay &&
    previousProps.chartBackgroundColor === nextProps.chartBackgroundColor &&
    previousProps.priceScaleStep === nextProps.priceScaleStep &&
    previousProps.priceScaleLevels === nextProps.priceScaleLevels &&
    previousProps.enableSyntheticFallback ===
    nextProps.enableSyntheticFallback &&
    previousProps.pinPriceScale === nextProps.pinPriceScale &&
    previousProps.showLiveMarker === nextProps.showLiveMarker &&
    previousProps.showLivePriceLine === nextProps.showLivePriceLine &&
    previousProps.showOrderTimeMarkers === nextProps.showOrderTimeMarkers &&
    areActiveOrdersEqual(previousProps.activeOrders, nextProps.activeOrders) &&
    areReferencePriceLinesEqual(
      previousProps.referencePriceLines,
      nextProps.referencePriceLines,
    ) &&
    previousProps.onOrderSettled === nextProps.onOrderSettled &&
    previousProps.cardClassName === nextProps.cardClassName &&
    previousProps.stageClassName === nextProps.stageClassName &&
    previousProps.canvasClassName === nextProps.canvasClassName &&
    previousProps.overlayClassName === nextProps.overlayClassName
  );
}

